import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
  BokkzWageDataService,
  Dienstverband,
  DienstverbandRedenEindeArbeidsverhouding,
  datumUitDienstNietVoorDatumInDienstValidator,
  WerknemerEntity,
} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormControl, FormGroup} from '@angular/forms';
import {DetailsConfig, SharedUiDetailsModule} from '@snelstart/ui-details';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiTabModule} from '@snelstart/ui-tab';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiIconModule} from '@snelstart/ui-icon';
import {filter, Observable, shareReplay, take, tap} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
  selector: 'snelstart-venture-fiscaal-dienstverband',
  standalone: true,
  imports: [
    CommonModule,
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SharedUiTabModule,
    SharedUiDialogModule,
    SharedUiIconModule
  ],
  templateUrl: './dienstverband.component.html',
  styleUrl: './dienstverband.component.scss'
})
export class DienstverbandComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  @Input() selectedTabDv = 0;
  private readonly destroyRef = inject(DestroyRef);
  private readonly dataService = inject(BokkzWageDataService);
  private werknemerEntity = {} as WerknemerEntity;

  public dienstverbandFg = new FormGroup({
    ikv: new FormControl<string | null>(null),
    geboorteDatum: new FormControl<Date | null>(null),
    datumInDienst: new FormControl<Date | null>(null),
    datumUitDienst: new FormControl<Date | null>(null),
    redenEindeArbeidsverhouding: new FormControl<DienstverbandRedenEindeArbeidsverhouding | null>(null),
  }, {
    validators: [
      datumUitDienstNietVoorDatumInDienstValidator(),
    ]
  });

  public dialogVisible = false;
  public selectedTab = 0;

  public detailsConfig = DetailsConfig.create({
    hideArrowButtons: false,
  });

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    { value: true, label: 'Ja' },
    { value: false, label: 'Nee' }
  ];

  public optionsUitDienst: SelectFormControlItem<DienstverbandRedenEindeArbeidsverhouding>[] = [
    {
        value: DienstverbandRedenEindeArbeidsverhouding.OPZEGGING_ONT_SLAG_PROEFTIJD,
        label: '(1) Opzegging/ontslag door de werkgever binnen de proeftijd'
    },
      {
          value: DienstverbandRedenEindeArbeidsverhouding.ONTBINDING_RECHTER_WERKGEVER,
          label: '(3) Ontbinding door rechter op verzoek van de werkgever'
      },
      {
          value: DienstverbandRedenEindeArbeidsverhouding.BEËINDIGING_WEDERZIJDS_GOEDVINDEN,
          label: '(4) Beëindiging arbeidsverhouding met wederzijds goedvinden op initiatief van de werkgever'
      },
      {
          value: DienstverbandRedenEindeArbeidsverhouding.OPZEGGING_LANGDURIGE_ARBEIDSONGESCHIKTHEID,
          label: '(5) Opzegging/ontslag door de werkgever vanwege langdurige arbeidsongeschiktheid'
      },
      {
          value: DienstverbandRedenEindeArbeidsverhouding.OPZEGGING_BEDRIJFSECONOMISCHE_REDENEN,
          label: '(6) Opzegging/ontslag door de werkgever vanwege bedrijfseconomische redenen'
      },
      {
          value: DienstverbandRedenEindeArbeidsverhouding.EINDE_OPZEGGING_WERKNEMER,
          label: '(20) Einde arbeidsverhouding door opzegging werknemer, door toedoen werknemer of op initiatief van de werknemer'
      },
      {
          value: DienstverbandRedenEindeArbeidsverhouding.OPZEGGING_DRINGENDE_REDEN,
          label: '(21) Opzegging/ontslag door werkgever om dringende reden (ontslag op staande voet)'
      },
      {
          value: DienstverbandRedenEindeArbeidsverhouding.EINDE_VERSTRIJKEN_DUUR,
          label: '(30) Einde van rechtswege vanwege verstrijken duur arbeidsverhouding voor bepaalde tijd'
      },
      {
          value: DienstverbandRedenEindeArbeidsverhouding.EINDE_VAN_RECHTSWEGE_VANWEGE_PENSIONERING,
          label: '(32) Einde van rechtswege vanwege pensionering'
      },
      {
          value: DienstverbandRedenEindeArbeidsverhouding.EINDE_VAN_RECHTSWEGE_VANWEGE_OVERLIJDEN,
          label: '(33) Einde van rechtswege vanwege overlijden'
      },
      {
          value: DienstverbandRedenEindeArbeidsverhouding.EINDE_VAN_RECHTSWEGE_VANWEGE_OM_ANDERE_REDEN,
          label: '(34) Einde van rechtswege vanwege een andere reden (intreden ontbindende voorwaarde e.d.)'
      },
      {
          value: DienstverbandRedenEindeArbeidsverhouding.EINDE_UIZENDOVEREEENKOMST_ZIEKTE,
          label: '(40) Einde uitzendovereenkomst, inroepen uitzendbeding door inlener vanwege ziekte uitzendkracht'
      },
      {
          value: DienstverbandRedenEindeArbeidsverhouding.EINDE_UIZENDOVEREEENKOMST_ANDERE_REDEN,
          label: '(41) Einde uitzendovereenkomst, inroepen uitzendbeding door inlener om een andere reden'
      },
      {
          value: DienstverbandRedenEindeArbeidsverhouding.ONTSLAG_PUBLIEKRECHTELIJK_ONBEKWAAMHEID,
          label: '(50) Ontslag publiekrechtelijke aanstelling vanwege onbekwaamheid of ongeschiktheid'
      },
      {
          value: DienstverbandRedenEindeArbeidsverhouding.ONTSLAG_PUBLIEKRECHTELIJK_LEEFTIJD,
          label: '(51) Ontslag publiekrechtelijke aanstelling vanwege leeftijd/pensionering'
      },
      {
          value: DienstverbandRedenEindeArbeidsverhouding.ARBEIDSVERHOUDING_LOOPT_DOOR_ADMIN_BEEINDIGD,
          label: '(90) De arbeidsverhouding loopt door, maar de inkomstenverhouding is administratief beëindigd'
      },
      {
          value: DienstverbandRedenEindeArbeidsverhouding.ARBEIDSVERHOUDING_NIEUWE_WERKGEVER,
          label: '(91) De arbeidsverhouding loopt door bij een nieuwe werkgever'
      },
      {
          value: DienstverbandRedenEindeArbeidsverhouding.ARBEIDSVERHOUDING_ZONDER_ONDERBREKING,
          label: '(92) De arbeidsverhouding wordt zonder onderbreking opgevolgd door een nieuwe arbeidsverhouding bij dezelfde werkgever'
      },
      {
          value: DienstverbandRedenEindeArbeidsverhouding.ANDERE_REDEN,
          label: '(99) Een andere, hiervoor niet genoemde reden'
      }
  ];

  readonly werknemerEntity$: Observable<WerknemerEntity> =
    this.dataService.werknemerEntity$.pipe(
      filter((data): data is WerknemerEntity => !!data?.id),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly selectedTab$: Observable<number> =
    this.dataService.selectedTab$.pipe(
      tap((value) => {
        this.selectedTab = value;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit() {
    this.werknemerEntity$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((data) => {
      this.werknemerEntity = data;
      const dv = data.werknemerGegevens?.dienstverbanden?.[0];
      this.dienstverbandFg.patchValue({
        ikv: dv?.ikv != null ? String(dv.ikv) : null,
        geboorteDatum: dv?.geboorteDatum ?? null,
        datumInDienst: dv?.datumInDienst ?? null,
        datumUitDienst: dv?.datumUitDienst ?? null,
        redenEindeArbeidsverhouding: dv?.redenEindeArbeidsverhouding ?? null,
      }, {emitEvent: false});
    });
    this.selectedTab$.subscribe();
  }

  ngOnDestroy(): void {
    this.saveForm();
  }

  public heeftDatumUitDienstVoorDatumInDienstError(): boolean {
    return this.dienstverbandFg.hasError('datumUitDienstVoorDatumInDienst');
  }

  public heeftGeldigeDatumUitDienst(): boolean {
    return !!this.dienstverbandFg.controls.datumUitDienst.value &&
      !this.heeftDatumUitDienstVoorDatumInDienstError();
  }

  public saveForm(): void {
    this.dienstverbandFg.updateValueAndValidity({ emitEvent: false });

    if (this.heeftDatumUitDienstVoorDatumInDienstError()) {
      return;
    }
    const datumUitDienst = this.dienstverbandFg.getRawValue().datumUitDienst;
    const redenEindeArbeidsverhouding = this.dienstverbandFg.getRawValue().redenEindeArbeidsverhouding;

    this.dataService.patchWerknemerEntity((current) => {
      const baseEntity = current?.werknemerGegevens ? current : this.werknemerEntity;
      const eersteDienstverband = baseEntity.werknemerGegevens?.dienstverbanden?.at(0);

      if (!baseEntity.werknemerGegevens || !eersteDienstverband) {
        return baseEntity;
      }

      const dienstverband = {
        ...eersteDienstverband,
        dga: false,
        datumUitDienst,
        redenEindeArbeidsverhouding,
      } as Dienstverband;

      const nextWerknemer = {
        ...baseEntity,
        werknemerGegevens: {
          ...baseEntity.werknemerGegevens,
          dienstverbanden: [dienstverband]
        }
      } as WerknemerEntity;

      this.werknemerEntity = nextWerknemer;
      return nextWerknemer;
    });
  }
}
